import { useState, useEffect } from 'react'
import { X, Globe, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { useFAQTranslations, useFAQTranslation } from '../../hooks/useFAQ'
import { showSuccessToast, showErrorToast } from '../../utils/toast'

// Langues supportées (ISO 639-1)
const SUPPORTED_LANGUAGES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' }
]

export default function FAQTranslationModal({ isOpen, onClose, faq, categories = [] }) {
  const [selectedLanguage, setSelectedLanguage] = useState('')
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: ''
  })
  const [errors, setErrors] = useState({})
  const [isEditing, setIsEditing] = useState(false)

  const { translations, loading: translationsLoading, createTranslation, updateTranslation, deleteTranslation, refetch } = useFAQTranslations(faq?.id)
  const { translation: currentTranslation, loading: translationLoading, refetch: refetchTranslation } = useFAQTranslation(
    faq?.id,
    selectedLanguage,
    { immediate: false }
  )

  // Réinitialiser le formulaire quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && faq) {
      setSelectedLanguage('')
      setFormData({
        question: '',
        answer: '',
        category: ''
      })
      setErrors({})
      setIsEditing(false)
      refetch()
    }
  }, [isOpen, faq, refetch])

  // Charger la traduction quand une langue est sélectionnée
  useEffect(() => {
    if (selectedLanguage && faq?.id) {
      refetchTranslation()
    }
  }, [selectedLanguage, faq?.id, refetchTranslation])

  // Mettre à jour le formulaire quand une traduction est chargée
  useEffect(() => {
    if (currentTranslation) {
      setFormData({
        question: currentTranslation.question || '',
        answer: currentTranslation.answer || '',
        category: currentTranslation.category || ''
      })
      setIsEditing(true)
    } else if (selectedLanguage) {
      // Nouvelle traduction - pré-remplir avec les valeurs de la FAQ originale
      setFormData({
        question: faq?.question || '',
        answer: faq?.answer || '',
        category: faq?.category || ''
      })
      setIsEditing(false)
    }
  }, [currentTranslation, selectedLanguage, faq])

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language)
    setErrors({})
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validate = () => {
    const newErrors = {}
    
    if (!selectedLanguage) {
      newErrors.language = 'Veuillez sélectionner une langue'
    }

    if (!formData.question.trim()) {
      newErrors.question = 'La question est requise'
    } else if (formData.question.length > 500) {
      newErrors.question = 'La question ne peut pas dépasser 500 caractères'
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'La réponse est requise'
    } else if (formData.answer.length > 5000) {
      newErrors.answer = 'La réponse ne peut pas dépasser 5000 caractères'
    }

    if (formData.category && formData.category.length > 100) {
      newErrors.category = 'La catégorie ne peut pas dépasser 100 caractères'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const translationData = {
      language: selectedLanguage,
      question: formData.question.trim(),
      answer: formData.answer.trim(),
      ...(formData.category && { category: formData.category.trim() })
    }

    let result
    if (isEditing) {
      result = await updateTranslation(selectedLanguage, translationData)
    } else {
      result = await createTranslation(translationData)
    }

    if (result.success) {
      showSuccessToast({ 
        message: isEditing 
          ? 'Traduction mise à jour avec succès' 
          : 'Traduction créée avec succès' 
      })
      await refetch()
      if (isEditing) {
        await refetchTranslation()
      } else {
        setIsEditing(true)
      }
    } else {
      showErrorToast({ message: result.error || 'Erreur lors de l\'enregistrement' })
    }
  }

  const handleDelete = async () => {
    if (!selectedLanguage) return

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la traduction en ${SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.name || selectedLanguage} ?`)) {
      return
    }

    const result = await deleteTranslation(selectedLanguage)
    if (result.success) {
      showSuccessToast({ message: 'Traduction supprimée avec succès' })
      setSelectedLanguage('')
      setFormData({
        question: '',
        answer: '',
        category: ''
      })
      setIsEditing(false)
      await refetch()
    } else {
      showErrorToast({ message: result.error || 'Erreur lors de la suppression' })
    }
  }

  // Obtenir les langues déjà traduites
  const translatedLanguages = translations.map(t => t.language)
  const availableLanguages = SUPPORTED_LANGUAGES.filter(lang => 
    !translatedLanguages.includes(lang.code) || lang.code === selectedLanguage
  )

  if (!isOpen) return null

  const isLoading = translationsLoading || translationLoading
  const selectedLangInfo = SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Gérer les traductions
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              FAQ: {faq?.question || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Langues disponibles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Globe className="w-4 h-4 inline mr-2" />
              Langues disponibles
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isTranslated = translatedLanguages.includes(lang.code)
                const isSelected = selectedLanguage === lang.code
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-[#141835] bg-[#141835] text-white'
                        : isTranslated
                        ? 'border-green-300 bg-green-50 hover:border-green-400'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{lang.flag}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{lang.name}</div>
                        <div className="text-xs opacity-75">{lang.code.toUpperCase()}</div>
                      </div>
                      {isTranslated && (
                        <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">
                          ✓
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
            {errors.language && (
              <p className="mt-2 text-sm text-red-600">{errors.language}</p>
            )}
          </div>

          {/* Formulaire de traduction */}
          {selectedLanguage && (
            <form onSubmit={handleSubmit} className="space-y-6 border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditing ? 'Modifier la traduction' : 'Créer une nouvelle traduction'}
                  {selectedLangInfo && (
                    <span className="ml-2 text-base font-normal text-gray-500">
                      ({selectedLangInfo.flag} {selectedLangInfo.name})
                    </span>
                  )}
                </h3>
                {isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    disabled={isLoading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>

              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.question}
                  onChange={(e) => handleChange('question', e.target.value)}
                  rows={2}
                  disabled={isLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.question ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Entrez la question traduite..."
                />
                {errors.question && (
                  <p className="mt-1 text-sm text-red-600">{errors.question}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.question.length}/500 caractères
                </p>
              </div>

              {/* Answer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Réponse <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => handleChange('answer', e.target.value)}
                  rows={6}
                  disabled={isLoading}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                    errors.answer ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Entrez la réponse traduite..."
                />
                {errors.answer && (
                  <p className="mt-1 text-sm text-red-600">{errors.answer}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.answer.length}/5000 caractères
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie (optionnel)
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    disabled={isLoading}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    disabled={isLoading}
                    placeholder="Ou saisir une nouvelle catégorie"
                    className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent ${
                      errors.category ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.category.length}/100 caractères
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Fermer
                </Button>
                <Button
                  type="submit"
                  className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? 'Enregistrement...' : (isEditing ? 'Mettre à jour' : 'Créer')}
                </Button>
              </div>
            </form>
          )}

          {/* Liste des traductions existantes */}
          {translations.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Traductions existantes ({translations.length})
              </h3>
              <div className="space-y-2">
                {translations.map((translation) => {
                  const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === translation.language)
                  return (
                    <div
                      key={translation.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{langInfo?.flag || '🌐'}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {langInfo?.name || translation.language.toUpperCase()}
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-md">
                            {translation.question}
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleLanguageChange(translation.language)}
                      >
                        Modifier
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
